/**
 * S4 — Industry Pack catalog (`/ai-studio/packs`), WF-F2 steps 1–2.
 *
 * Lists platform and tenant packs and creates new draft pack shells. Search and
 * status filtering are client-side (same rationale as the agent catalog).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { CreatePackForm, PackTable } from '@/components/ai-studio/packs';
import { createIndustryPack, listIndustryPacks } from '@/services/aiStudioAgentService';
import { STUDIO_LIFECYCLE_STATUSES } from '@/types/aiStudioAgent';
import type {
  AiStudioIndustryPack,
  CreatePackRequest,
  StudioLifecycleStatus,
} from '@/types/aiStudioAgent';

const AIStudioPackCatalogPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [packs, setPacks] = useState<AiStudioIndustryPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StudioLifecycleStatus | ''>('');
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listIndustryPacks()
      .then((items) => {
        if (!cancelled) setPacks(items);
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.packs.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visiblePacks = useMemo(() => {
    const term = search.trim().toLowerCase();
    return packs.filter((pack) => {
      const matchesStatus = !statusFilter || pack.lifecycleStatus === statusFilter;
      const matchesTerm =
        !term ||
        pack.code.toLowerCase().includes(term) ||
        pack.name.toLowerCase().includes(term);
      return matchesStatus && matchesTerm;
    });
  }, [packs, search, statusFilter]);

  const handleCreate = useCallback(
    async (request: CreatePackRequest) => {
      setCreating(true);
      setErrorKey(null);
      try {
        const pack = await createIndustryPack(request);
        navigate(`/ai-studio/packs/${pack.id}`);
      } catch {
        setErrorKey('aiStudio.packs.createFailed');
      } finally {
        setCreating(false);
      }
    },
    [navigate]
  );

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[var(--color-surface-text)]">
            {t('aiStudio.packs.title')}
          </h1>
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.packs.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((current) => !current)}
          className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
        >
          {t('aiStudio.packs.createPack')}
        </button>
      </header>

      {errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {t(errorKey)}
        </div>
      )}

      {showForm && (
        <CreatePackForm
          submitting={creating}
          onSubmit={(request) => void handleCreate(request)}
          onCancel={() => setShowForm(false)}
        />
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1">
          <label
            htmlFor="pack-search"
            className="mb-1 block text-base font-medium text-[var(--color-surface-text)]"
          >
            {t('aiStudio.packs.search')}
          </label>
          <input
            id="pack-search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-full rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
          />
        </div>
        <div>
          <label
            htmlFor="pack-status"
            className="mb-1 block text-base font-medium text-[var(--color-surface-text)]"
          >
            {t('aiStudio.packs.filterStatus')}
          </label>
          <select
            id="pack-status"
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value as StudioLifecycleStatus | '')
            }
            className="rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
          >
            <option value="">{t('aiStudio.packs.filterAll')}</option>
            {STUDIO_LIFECYCLE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.packs.loading')}
        </p>
      ) : visiblePacks.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.packs.empty')}
        </p>
      ) : (
        <PackTable
          packs={visiblePacks}
          onSelect={(packId) => navigate(`/ai-studio/packs/${packId}`)}
        />
      )}
    </main>
  );
};

export default AIStudioPackCatalogPage;
